import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { useCanEdit } from '@/lib/can-edit';
import { GenerateSnapshotDialog } from './GenerateSnapshotDialog';

type Props =
  | { mode: 'live'; updatedAt: string | null; stamp?: string | null }
  | { mode: 'archive' }
  | {
      mode: 'snapshot';
      generatedAt: string;
      generatedBy: string;
      snapshotId: string;
      snapshotName: string | null;
      stamp?: string | null;
    };

export function ReportHeader(props: Props) {
  const canEdit = useCanEdit();
  return (
    <header className="flex flex-wrap items-end justify-between gap-6 border-b border-border pb-gap-element-lg">
      <div>
        <p className="text-label uppercase tracking-[0.08em] text-accent">
          {props.mode === 'snapshot' ? 'Snapshot' : 'Compliance posture'}
        </p>
        <h1 className="mt-2 font-display text-h1 font-bold tracking-tight">
          {props.mode === 'archive'
            ? 'Snapshot archive'
            : props.mode === 'snapshot'
              ? props.snapshotName ?? new Date(props.generatedAt).toLocaleString()
              : 'Live report'}
        </h1>
        {props.mode === 'snapshot' && props.snapshotName ? (
          <p className="mt-1 text-body-sm text-text-secondary">
            {new Date(props.generatedAt).toLocaleString()}
          </p>
        ) : null}
        {props.mode !== 'archive' && props.stamp ? (
          <p className="mt-2 text-body font-medium text-text-primary" data-testid="catalog-stamp">
            {props.stamp}
          </p>
        ) : null}
        {props.mode === 'live' ? (
          <p className="mt-2 text-body text-text-secondary">
            Last updated {props.updatedAt ? new Date(props.updatedAt).toLocaleString() : '—'}
          </p>
        ) : null}
        {props.mode === 'snapshot' ? (
          <p className="mt-2 text-body text-text-secondary">
            Generated by {props.generatedBy}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        {props.mode === 'snapshot' ? (
          <>
            <Link
              to="/report/snapshots"
              className="text-body-sm font-medium text-text-secondary hover:text-accent"
            >
              ← Back to snapshots
            </Link>
            <Button
              variant="dark"
              onClick={() => downloadSnapshotPdf(props.snapshotId)}
            >
              Download PDF
            </Button>
          </>
        ) : (
          <ModeToggle current={props.mode} />
        )}
        {props.mode === 'live' && canEdit ? (
          <GenerateSnapshotDialog
            trigger={<Button variant="dark">Generate snapshot</Button>}
          />
        ) : null}
      </div>
    </header>
  );
}

function ModeToggle({ current }: { current: 'live' | 'archive' }) {
  return (
    <div
      role="tablist"
      className="inline-flex rounded-md border border-border bg-surface p-1 text-body-sm"
    >
      <Link
        to="/report"
        role="tab"
        aria-selected={current === 'live'}
        className={cn(
          'rounded px-3 py-1 transition',
          current === 'live'
            ? 'bg-text-primary text-white'
            : 'text-text-secondary hover:text-text-primary',
        )}
      >
        Live
      </Link>
      <Link
        to="/report/snapshots"
        role="tab"
        aria-selected={current === 'archive'}
        className={cn(
          'rounded px-3 py-1 transition',
          current === 'archive'
            ? 'bg-text-primary text-white'
            : 'text-text-secondary hover:text-text-primary',
        )}
      >
        Snapshots
      </Link>
    </div>
  );
}

async function downloadSnapshotPdf(id: string) {
  const { downloadWithCompanyHeader } = await import('@/lib/download');
  await downloadWithCompanyHeader(
    `/api/Snapshots/${id}/export.pdf`,
    `hexdi-snapshot-${id}.pdf`,
  );
}
